import { ChevronLeftIcon, ChevronDownIcon, Copy, EyeIcon, Bluetooth, Package, Phone, Truck } from "@/components/icons";
import {
  Badge,
  Button,
  Input,
  Skeleton,
  toast,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { PackQueueOrderDTO } from "@/modules/fulfillment/domain/types";
import {
  useMarkOrderReady,
  useMarkOrderShipped,
} from "@/modules/orders/presentation/api/queries";
import {
  DELIVERY_METHOD_META,
  formatDeliverySummary,
  getOrderStatusMeta,
  PAYMENT_TYPE_META,
  orderStatusLabel,
} from "@/modules/orders/presentation/ui/order-status";
import { formatLak } from "@/shared/lib/currency";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import type { BatchPrintOrderRef } from "@/modules/chat-tools/presentation/lib/load-batch-label-items";
import { BatchBluetoothPrintDialog } from "@/modules/chat-tools/presentation/ui/BatchBluetoothPrintDialog";
import { isWebBluetoothSupported } from "@/modules/chat-tools/presentation/lib/web-bluetooth";
import { usePackQueueQuery } from "../api/queries";
import type { PackQueueLineDTO } from "@/modules/fulfillment/domain/types";

type PackPhase = "pack" | "ship";

function toBatchPrintOrders(
  orders: PackQueueOrderDTO[],
): BatchPrintOrderRef[] {
  return orders.map((order) => ({
    id: order.id,
    orderNumber: order.orderNumber,
    customerName: order.customerName,
  }));
}

function PackLineItem({ item }: { item: PackQueueLineDTO }) {
  const [open, setOpen] = useState(false);
  const hasChecklist =
    (item.variantType === "bundle" || item.variantType === "ad_hoc_kit") &&
    (item.bundleComponents?.length ?? 0) > 0;

  return (
    <li
      className={cn(
        "text-sm",
        item.isBonus && "text-muted-foreground",
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 flex-1 leading-snug">
          {item.productName}
          {item.variantName ? (
            <span className="text-muted-foreground"> · {item.variantName}</span>
          ) : null}
          {item.variantType === "bundle" || item.variantType === "ad_hoc_kit" ? (
            <Badge variant="outline" className="ml-2 align-middle text-xs">
              {item.variantType === "ad_hoc_kit" ? "ຊຸດກຳນົດເອງ" : "ເຊັດ"}
            </Badge>
          ) : null}
          {item.isBonus ? (
            <Badge variant="outline" className="ml-2 align-middle text-xs">
              ແຖມ
            </Badge>
          ) : null}
        </span>
        <span className="shrink-0 font-medium tabular-nums">×{item.quantity}</span>
      </div>
      {hasChecklist ? (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground"
          >
            <ChevronDownIcon
              className={cn("size-3.5 transition-transform", open && "rotate-180")}
            />
            ລາຍການໃນຊຸດ
          </button>
          {open ? (
            <ul className="mt-2 ml-1 flex flex-col gap-1 border-secondary/15 border-l pl-3 text-muted-foreground text-xs">
              {item.bundleComponents?.map((row) => (
                <li
                  key={`${row.productName}|${row.variantName}|${row.quantity}`}
                  className="flex justify-between gap-2"
                >
                  <span>
                    {row.productName} · {row.variantName}
                  </span>
                  <span className="tabular-nums">×{row.quantity}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function PackOrderCard({ order }: { order: PackQueueOrderDTO }) {
  const statusMeta = getOrderStatusMeta(order.status, order.paymentType);

  return (
    <article className="brand-card flex flex-col gap-4 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-display text-2xl text-foreground leading-tight">
            {order.customerName}
          </p>
          {order.customerPhone ? (
            <p className="mt-1 flex items-center gap-1.5 text-muted-foreground text-sm">
              <Phone className="size-4 shrink-0" />
              {order.customerPhone}
            </p>
          ) : null}
          <p className="mt-2 font-mono text-muted-foreground text-xs">
            {order.orderNumber}
          </p>
        </div>
        <span className="shrink-0 font-display text-xl tabular-nums">
          {formatLak(order.total)}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge variant={statusMeta.variant} className="text-sm">
          {orderStatusLabel(order.status, order.paymentType)}
        </Badge>
        <Badge
          variant={PAYMENT_TYPE_META[order.paymentType].variant}
          className="text-sm"
        >
          {PAYMENT_TYPE_META[order.paymentType].shortLabel}
        </Badge>
        <Badge
          variant={DELIVERY_METHOD_META[order.deliveryMethod].variant}
          className="text-sm"
        >
          {DELIVERY_METHOD_META[order.deliveryMethod].shortLabel}
        </Badge>
      </div>

      <div className="text-muted-foreground text-sm">
        {formatDeliverySummary(order)}
      </div>

      <ul className="flex flex-col gap-2 border-secondary/15 border-t pt-4">
        {order.items.map((item) => (
          <PackLineItem
            key={`${item.variantId}|${item.quantity}|${item.isBonus}`}
            item={item}
          />
        ))}
      </ul>
    </article>
  );
}

export function PackModePage() {
  const nav = useNavigate();
  const canManage = useActionPermission(["orders:write"]);
  const bluetoothSupported = isWebBluetoothSupported();
  const queue = usePackQueueQuery();
  const markReady = useMarkOrderReady();
  const markShipped = useMarkOrderShipped();
  const [phase, setPhase] = useState<PackPhase>("pack");
  const [index, setIndex] = useState(0);
  const [focusOrderId, setFocusOrderId] = useState<string | null>(null);
  const [tracking, setTracking] = useState("");
  const [batchPrintOpen, setBatchPrintOpen] = useState(false);
  const [batchPrintOrders, setBatchPrintOrders] = useState<BatchPrintOrderRef[]>(
    [],
  );
  const trackingRef = useRef<HTMLInputElement>(null);

  const allOrders = queue.data?.data ?? [];
  const meta = queue.data?.meta;
  const packOrders = useMemo(
    () => allOrders.filter((o) => o.status === "paid"),
    [allOrders],
  );
  const shipOrders = useMemo(
    () => allOrders.filter((o) => o.status === "ready_to_ship"),
    [allOrders],
  );
  const phaseOrders = phase === "pack" ? packOrders : shipOrders;
  const current = useMemo(() => {
    if (focusOrderId) {
      const focused = phaseOrders.find((o) => o.id === focusOrderId);
      if (focused) return focused;
    }
    return phaseOrders[index] ?? null;
  }, [focusOrderId, index, phaseOrders]);

  useEffect(() => {
    if (phaseOrders.length === 0) {
      setIndex(0);
      return;
    }
    if (focusOrderId) {
      const idx = phaseOrders.findIndex((o) => o.id === focusOrderId);
      if (idx >= 0) {
        setIndex(idx);
        return;
      }
      setFocusOrderId(null);
    }
    if (index >= phaseOrders.length) {
      setIndex(phaseOrders.length - 1);
    }
  }, [focusOrderId, index, phaseOrders]);

  useEffect(() => {
    if (phase !== "ship" || !current) return;
    setTracking(current.trackingNumber ?? "");
    const t = window.setTimeout(() => trackingRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, [phase, current?.id]);

  function goToOrder(id: string) {
    nav({ to: "/app/orders/$id", params: { id } });
  }

  function callPhone(phone: string) {
    window.location.href = `tel:${phone.replace(/\s/g, "")}`;
  }

  async function copyPhone(phone: string) {
    try {
      await navigator.clipboard.writeText(phone);
      toast.success("ຄັດລອກເບີແລ້ວ");
    } catch {
      toast.error("ຄັດລອກບໍ່ສຳເລັດ");
    }
  }

  function switchPhase(next: PackPhase) {
    setPhase(next);
    setIndex(0);
    setFocusOrderId(null);
    setTracking("");
  }

  function handleMarkReady() {
    if (!current || current.status !== "paid" || !canManage) return;
    const packedId = current.id;
    markReady.mutate(
      { id: packedId },
      {
        onSuccess: () => {
          toast.success("ແພັກສຳເລັດ — ໃສ່ Tracking ຕໍ່ໄດ້");
          setPhase("ship");
          setFocusOrderId(packedId);
          setIndex(0);
          setTracking("");
        },
        onError: (e) =>
          toast.error("ອັບເດດບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
      },
    );
  }

  function handleMarkShipped() {
    if (!current || current.status !== "ready_to_ship" || !canManage) return;
    const trackingValue = tracking.trim() || null;
    if (current.deliveryMethod === "carrier" && !trackingValue) {
      toast.error("ກະລຸນາໃສ່ເລກ Tracking");
      trackingRef.current?.focus();
      return;
    }
    markShipped.mutate(
      { id: current.id, input: { trackingNumber: trackingValue } },
      {
        onSuccess: () => {
          toast.success("ບັນທຶກສົ່ງແລ້ວ");
          setFocusOrderId(null);
          setTracking("");
          if (shipOrders.length <= 1 && packOrders.length > 0) {
            setPhase("pack");
            setIndex(0);
          } else {
            setIndex(0);
          }
        },
        onError: (e) =>
          toast.error("ອັບເດດບໍ່ສຳເລັດ", {
            description: e instanceof Error ? e.message : undefined,
          }),
      },
    );
  }

  function goNext() {
    setFocusOrderId(null);
    setIndex((i) => Math.min(i + 1, Math.max(phaseOrders.length - 1, 0)));
  }

  function goPrev() {
    setFocusOrderId(null);
    setIndex((i) => Math.max(i - 1, 0));
  }

  function openBatchPrint(orders: PackQueueOrderDTO[]) {
    setBatchPrintOrders(toBatchPrintOrders(orders));
    setBatchPrintOpen(true);
  }

  const paidCount = meta?.paidCount ?? packOrders.length;
  const readyCount = meta?.readyCount ?? shipOrders.length;
  const pendingText =
    phase === "pack"
      ? `ຄ້າງແພັກ ${paidCount}`
      : `ຄ້າງສົ່ງ ${readyCount}`;

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background">
      <header className="sticky top-0 z-10 border-b bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 rounded-full"
            onClick={() => nav({ to: "/app/fulfillment" })}
            aria-label="ກັບໄປຈັດສົ່ງ"
          >
            <ChevronLeftIcon />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="font-display text-lg">ໂໝດແພັກ</h1>
            <p className="truncate text-muted-foreground text-xs">
              {meta
                ? `ລໍຖ້າແພັກ ${paidCount} · ພ້ອມສົ່ງ ${readyCount}`
                : "ກຳລັງໂຫຼດ..."}
            </p>
          </div>
          <Package className="size-5 shrink-0 text-secondary" />
          {bluetoothSupported && phaseOrders.length > 0 ? (
            <Button
              variant="outline"
              size="icon"
              className="shrink-0 rounded-full"
              title="ພິມປ້າຍຊຸດ"
              onClick={() => openBatchPrint(phaseOrders)}
            >
              <Bluetooth />
            </Button>
          ) : null}
        </div>

        <div className="mx-auto mt-3 grid max-w-lg grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => switchPhase("pack")}
            className={cn(
              "flex min-h-11 items-center justify-center gap-2 rounded-full border font-medium text-sm transition-colors",
              phase === "pack"
                ? "border-secondary bg-secondary text-secondary-foreground"
                : "border-secondary/30 hover:bg-secondary/10",
            )}
          >
            <Package className="size-4" />
            ລໍຖ້າແພັກ
            <span className="tabular-nums">({paidCount})</span>
          </button>
          <button
            type="button"
            onClick={() => switchPhase("ship")}
            className={cn(
              "flex min-h-11 items-center justify-center gap-2 rounded-full border font-medium text-sm transition-colors",
              phase === "ship"
                ? "border-secondary bg-secondary text-secondary-foreground"
                : "border-secondary/30 hover:bg-secondary/10",
            )}
          >
            <Truck className="size-4" />
            ພ້ອມສົ່ງ
            <span className="tabular-nums">({readyCount})</span>
          </button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-lg flex-1 flex-col gap-4 px-4 py-4 pb-40">
        {queue.isLoading ? (
          <Skeleton className="min-h-[20rem] rounded-2xl" />
        ) : phaseOrders.length === 0 ? (
          <div className="brand-card flex flex-col items-center gap-3 p-8 text-center">
            <Package className="size-10 text-muted-foreground" />
            <p className="font-display text-lg">
              {phase === "pack" ? "ບໍ່ມີອໍເດີ້ລໍຖ້າແພັກ" : "ບໍ່ມີອໍເດີ້ພ້ອມສົ່ງ"}
            </p>
            <p className="text-muted-foreground text-sm">
              {phase === "pack" ? "ອໍເດີ້ທີ່ຊຳລະແລ້ວຈະປາກົດທີ່ນີ້" : "ແພັກເສັດແລ້ວຈະປາກົດທີ່ນີ້ເພື່ອໃສ່ Tracking"}
            </p>
            {phase === "pack" && readyCount > 0 ? (
              <Button className="mt-2 rounded-full" onClick={() => switchPhase("ship")}>
                ໄປໃສ່ Tracking ({readyCount})
              </Button>
            ) : phase === "ship" && paidCount > 0 ? (
              <Button className="mt-2 rounded-full" onClick={() => switchPhase("pack")}>
                ກັບໄປແພັກ ({paidCount})
              </Button>
            ) : (
              <Button asChild variant="outline" className="mt-2 rounded-full">
                <Link to="/app/fulfillment">ກັບໄປຈັດສົ່ງ</Link>
              </Button>
            )}
          </div>
        ) : current ? (
          <>
            <p className="text-center text-muted-foreground text-sm tabular-nums">
              {index + 1} / {phaseOrders.length}
              {phase === "ship" ? " · ໃສ່ Tracking" : ""}
            </p>
            <PackOrderCard order={current} />

            {phase === "ship" && canManage ? (
              <div className="brand-card flex flex-col gap-2 p-4">
                <label htmlFor="pack-tracking" className="font-medium text-sm">
                  ເລກ Tracking
                  {current.deliveryMethod === "carrier" ? " *" : ""}
                </label>
                <Input
                  ref={trackingRef}
                  id="pack-tracking"
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleMarkShipped();
                    }
                  }}
                  placeholder="ສະແກນ ຫລື ພິມເລກ Tracking..."
                  autoComplete="off"
                  inputMode="text"
                  className="h-12 text-base"
                />
                {current.deliveryMethod === "pickup" ? (
                  <p className="text-muted-foreground text-xs">
                    ຮັບທີ່ຮ້ານ — Tracking ບໍ່ບັງຄັບ
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="min-h-11 flex-1 rounded-full"
                onClick={() => goToOrder(current.id)}
              >
                <EyeIcon data-icon="inline-start" />
                ລາຍລະອຽດ
              </Button>
              {bluetoothSupported ? (
                <Button
                  variant="outline"
                  className="min-h-11 flex-1 rounded-full"
                  onClick={() => openBatchPrint([current])}
                >
                  <Bluetooth data-icon="inline-start" />
                  ພິມປ້າຍ
                </Button>
              ) : null}
              {current.customerPhone ? (
                <>
                  <Button
                    variant="outline"
                    className="min-h-11 flex-1 rounded-full"
                    onClick={() => {
                      const phone = current.customerPhone;
                      if (phone) callPhone(phone);
                    }}
                  >
                    <Phone data-icon="inline-start" />
                    ໂທ
                  </Button>
                  <Button
                    variant="outline"
                    className="min-h-11 flex-1 rounded-full"
                    onClick={() => {
                      const phone = current.customerPhone;
                      if (phone) void copyPhone(phone);
                    }}
                  >
                    <Copy data-icon="inline-start" />
                    ຄັດລອກ
                  </Button>
                </>
              ) : null}
            </div>
          </>
        ) : null}
      </main>

      {phaseOrders.length > 0 && current ? (
        <footer className="fixed inset-x-0 bottom-(--mobile-bottom-nav,0px) z-10 border-t bg-background/95 px-4 py-3 backdrop-blur supports-backdrop-filter:bg-background/80">
          <div className="mx-auto flex max-w-lg flex-col gap-2">
            {canManage && phase === "pack" && current.status === "paid" ? (
              <Button
                className="min-h-12 w-full rounded-full text-base"
                isLoading={markReady.isPending}
                onClick={handleMarkReady}
              >
                ແພັກສຳເລັດ
              </Button>
            ) : null}
            {canManage &&
            phase === "ship" &&
            current.status === "ready_to_ship" ? (
              <Button
                className="min-h-12 w-full rounded-full text-base"
                isLoading={markShipped.isPending}
                onClick={handleMarkShipped}
              >
                ສົ່ງແລ້ວ
              </Button>
            ) : null}

            <div className="flex items-center justify-between gap-2">
              <Button
                variant="outline"
                className="min-h-11 flex-1 rounded-full"
                disabled={index <= 0}
                onClick={goPrev}
              >
                ກ່ອນໜ້າ
              </Button>
              <span className="shrink-0 text-muted-foreground text-sm tabular-nums">
                {pendingText}
              </span>
              <Button
                variant="outline"
                className="min-h-11 flex-1 rounded-full"
                disabled={index >= phaseOrders.length - 1}
                onClick={goNext}
              >
                ຖັດໄປ
              </Button>
            </div>
          </div>
        </footer>
      ) : null}

      <BatchBluetoothPrintDialog
        open={batchPrintOpen}
        onOpenChange={setBatchPrintOpen}
        orders={batchPrintOrders}
        minimizable
        title={
          batchPrintOrders.length <= 1
            ? "ພິມປ້າຍ Bluetooth"
            : `ພິມປ້າຍຊຸດ (${batchPrintOrders.length})`
        }
      />
    </div>
  );
}
